import React, { useEffect, useState } from 'react';
import { useBrandingColors } from '../../hooks/useBrandingColors';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { useRoleAccess } from '../../hooks/useRoleAccess';
import api from '../services/api';
import HHKeySetupModal from './HHKeySetupModal';
import { redirectToHhAuthorization } from '../services/hhOAuthNavigation';

const HHAuthContent: React.FC = () => {
  const colors = useBrandingColors();
  const { isSuperAdmin } = useRoleAccess();
  const [authUrl, setAuthUrl] = useState('');
  const [currentStatus, setCurrentStatus] = useState<any>(null);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [isKeyModalOpen, setIsKeyModalOpen] = useState(false);

  useEffect(() => {
    void checkCurrentStatus();
    void fetchAuthUrl();
  }, []);

  const checkCurrentStatus = async () => {
    setCheckingStatus(true);
    try {
      const res = await api.get('/auth/hh/status');
      setCurrentStatus(res.data);
    } catch (e) {
      setCurrentStatus({ status: 'error', hasToken: null });
    } finally {
      setCheckingStatus(false);
    }
  };

  const fetchAuthUrl = async () => {
    try {
      const res = await api.get('/auth/hh/auth-url');
      setAuthUrl(res.data?.authUrl || '');
    } catch (e:any) {
      toast.error('Ошибка получения URL авторизации');
    }
  };

  const handleKeySetupSuccess = async () => {
    await checkCurrentStatus();
  };

  const hhStatus = currentStatus?.status;
  const isAuthorized = hhStatus === 'authorized';
  const isUnauthorized = hhStatus === 'unauthorized';

  return (
    <div className="space-y-6">
      {!checkingStatus && currentStatus && (
        <div
          className={`flex items-center gap-2 p-4 rounded-xl ${
            isAuthorized
              ? 'bg-green-50 text-green-800 border border-green-200'
              : isUnauthorized
                ? 'bg-yellow-50 text-yellow-800 border border-yellow-200'
                : 'bg-gray-50 text-gray-700 border border-gray-200'
          }`}
        >
          {isAuthorized ? (
            <>
              <CheckCircleIcon className="h-5 w-5" />
              <div>
                <p className="font-medium">HH.ru подключен</p>
              </div>
            </>
          ) : isUnauthorized ? (
            <>
              <XCircleIcon className="h-5 w-5" />
              <p className="font-medium">HH.ru не подключен</p>
            </>
          ) : (
            <>
              <XCircleIcon className="h-5 w-5" />
              <p className="font-medium">Статус HH.ru недоступен</p>
            </>
          )}
        </div>
      )}

      {authUrl ? (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-blue-800 font-medium mb-2">Для подключения HH.ru:</p>
            <ol className="list-decimal list-inside space-y-1 text-sm">
              <li>Нажмите кнопку "Авторизоваться в HH.ru"</li>
              <li>Войдите в свой аккаунт HeadHunter</li>
              <li>Разрешите доступ приложению</li>
              <li>После авторизации вы автоматически вернётесь в Relevanter</li>
            </ol>
          </div>
          
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => redirectToHhAuthorization(authUrl)}
              className="inline-flex items-center px-4 py-2 text-white font-medium rounded-3xl focus:outline-none"
              style={{ backgroundColor: colors.primary }}
            >
              Авторизоваться в HH.ru
            </button>
            
            {isSuperAdmin && (
              <button
                onClick={() => setIsKeyModalOpen(true)}
                className="inline-flex items-center px-4 py-2 text-gray-700 bg-gray-100 font-medium rounded-3xl hover:bg-gray-200 focus:outline-none"
              >
                Установить ключ
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="text-center text-gray-600">
          Загрузка...
        </div>
      )}

      <HHKeySetupModal
        isOpen={isKeyModalOpen}
        onClose={() => setIsKeyModalOpen(false)}
        onSuccess={handleKeySetupSuccess}
      />
    </div>
  );
};

export default HHAuthContent;
